"use client";

import React from "react";
import clsx from "clsx";
import { PiSpinner } from "react-icons/pi";

export type BUTTON_TYPE_SUBMIT = "submit";

type Props = {
    label: string;
    pendingLabel?: string;
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
    disabled?: boolean;
    pending?: boolean;
    icon?: React.ReactNode;
    className?: string;
    variant?: "default" | "light";
    size?: "default" | "small";
    onClickAction?: React.MouseEventHandler<HTMLButtonElement>;
};

export function formActionButtonClassNames({
    disabled,
    pending,
    className,
    variant = "default",
    size = "default",
}: {
    disabled?: boolean;
    pending?: boolean;
    className?: string;
    variant?: Props["variant"];
    size?: Props["size"];
}) {
    const isDisabled = !!disabled || !!pending;

    return clsx(
        "inline-flex items-center justify-center gap-2 rounded-md",
        "text-center font-jost font-semibold select-none",
        size === "small" ? "px-3 py-2 text-sm" : "md:min-w-52 px-8 py-3 text-lg",
        variant === "light" ? "bg-zinc-100 text-zinc-700" : "bg-black text-white",
        !pending && (variant === "light" ? "hover:bg-zinc-200" : "hover:bg-black/80"),
        pending && "animate-pulse",
        isDisabled ? "opacity-80 cursor-not-allowed" : "opacity-100",
        className,
    );
}

export function SubmitButton({
    label,
    pendingLabel,
    type = "submit",
    disabled,
    pending,
    icon,
    className,
    variant = "default",
    size = "default",
    onClickAction,
}: Props) {
    const isDisabled = !!disabled || !!pending;

    return (
        <button
            type={type}
            onClick={onClickAction}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            aria-busy={!!pending}
            className={formActionButtonClassNames({disabled, pending, className, variant, size})}>
            <span className="w-full select-none">
                {pending && pendingLabel ? pendingLabel : label}
            </span>
            {!pending && icon}
            {pending && <PiSpinner className="animate-spin" size={size === "small" ? "1.25rem" : "2rem"} />}
        </button>
    );
}
